import {
  Box,
  Container,
  Text,
  VStack,
  HStack,
  useDisclosure,
  Link,
  useBreakpointValue,
  Flex,
  Image,
} from '@chakra-ui/react'
// import VampireTeethSvg from '../assets/vampire-teeth.svg?react'
import { TinyAction } from './TinyAction'
import ReportModal from './ReportModal'
import { AgreementsModal } from './AgreementsModal'
import { useEffect, useMemo, useState } from 'react'
import { NetworkTypes } from '@provex/eip712-utils'
import { YOU_ARE_SACRIFICING } from 'sacrifice-utils/disclaimer'
import { useProjects } from '../contexts'
import { SacrificeData } from 'sacrifice-utils/types'
import { ProjectNetworkIdentifier } from '@provex/eip712-utils/keys'

const Footer: React.FC = () => {
  const { projectId, selectedProject } = useProjects()
  // Brand "strata" palette (semantic tokens auto-resolve light/dark) — matches
  // the header/nav. Previously hardcoded generic Chakra grays, which read as the
  // old, off-brand footer.
  const bgColor = 'bg.band'
  const borderColor = 'line.dark'
  const textColor = 'text.muted'
  const hoverColor = 'text.engraved'
  const [isOpen, setIsOpen] = useState(false)
  // Wallet-connect-capable variant of the report modal, lazy-loaded on first
  // open. The bare ReportModal cannot open the wallet connect flow (its
  // openAppKit prop is unset), so a disconnected visitor clicking "Connect
  // Wallet" hit a dead no-op. This pulls in the ~2 MB AppKit bundle only when
  // the report is actually opened, mirroring UserSacrificeDashboard.
  const [ReportModalWithAppKit, setReportModalWithAppKit] = useState<React.ComponentType<{
    onClose: () => void
    target: ProjectNetworkIdentifier | SacrificeData | null
    title?: string
  }> | null>(null)

  useEffect(() => {
    if (!isOpen) return
    if (ReportModalWithAppKit) return
    const loadReportModalWithAppKit = async () => {
      const { initializeAppKit } = await import('../utils/appkit-init')
      await initializeAppKit()
      const module = await import('./ReportModalWithAppKit')
      setReportModalWithAppKit(() => module.default)
    }
    loadReportModalWithAppKit().catch(console.error)
  }, [isOpen, ReportModalWithAppKit])
  // const breakpoint = useBreakpoint('lg')
  // useBreakpointValue({base: 'column', lg: 'row'})
  const direction = useBreakpointValue({ base: 'base', sm: 'sm', md: 'md' }, { ssr: false })
  const target = useMemo(() => {
    return !isOpen
      ? null
      : {
          projectId,
          chainId: 1,
          networkType: NetworkTypes.EVM,
        }
  }, [isOpen, projectId])
  const {
    isOpen: isDisclaimerOpen,
    onOpen: onDisclaimerOpen,
    onClose: onDisclaimerClose,
  } = useDisclosure()

  return (
    <>
      <Box as="footer" bg={bgColor} borderTop="1px" borderColor={borderColor} py={4} px={4}>
        <Container maxW="container.xl" px={0}>
          <VStack spacing={4}>
            <Flex
              direction={{ base: 'column', md: 'row' }}
              justify="space-between"
              align="center"
              w="100%"
              gap={4}
            >
              <Box textAlign="center">
                <Flex
                  justify="center"
                  gap={4}
                  direction={{ base: 'column', sm: 'row' }}
                  align="center"
                >
                  <Flex gap={4} direction={{ base: 'column', sm: 'row' }} align="center">
                    <HStack
                      spacing={0}
                      flexDir={{ base: 'column', md: 'row' }}
                      gap={{ base: 0, md: 2 }}
                    >
                      <Text fontSize="xs" color={textColor}>
                        © {new Date().getFullYear()}
                      </Text>
                      <HStack spacing={0}>
                        {/* <Image src="/vampire-teeth.svg" alt="orbius.cash" width={4} height={4} /> */}
                        <Image
                          src="/altar-favicon.svg"
                          alt="Altar"
                          width={4}
                          height={4}
                          style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }}
                        />
                        {/* <VampireTeethSvg style={{ color: textColor }} height={16} width={16} />
                        <Text fontSize="xs" color={textColor}>
                          orbius.cash
                        </Text> */}
                        <Text fontSize="xs" color={textColor}>
                          altar.fund
                        </Text>
                      </HStack>
                    </HStack>
                    <Text fontSize="xs" color={textColor} display={{ base: 'none', sm: 'block' }}>
                      •
                    </Text>
                    <HStack
                      spacing={0}
                      flexDir={{ base: 'column', md: 'row' }}
                      gap={{ base: 0, md: 4 }}
                    >
                      <Text fontSize="xs" color={textColor}>
                        Sacrifice Dashboard
                      </Text>
                      {direction === 'md' ? (
                        <Text fontSize="xs" color={textColor}>
                          •
                        </Text>
                      ) : null}
                      <Text fontSize="xs" color={textColor}>
                        All rights reserved
                      </Text>
                    </HStack>
                  </Flex>
                  <Text fontSize="xs" color={textColor} display={{ base: 'none', sm: 'block' }}>
                    •
                  </Text>
                  <Flex
                    justify="center"
                    gap={{ sm: 2, base: 4 }}
                    align="center"
                    direction={{ base: 'column', sm: 'row' }}
                  >
                    <Link
                      fontSize="xs"
                      color={textColor}
                      _hover={{ color: hoverColor }}
                      href="#"
                      onClick={e => {
                        e.preventDefault()
                        onDisclaimerOpen()
                      }}
                      cursor="pointer"
                    >
                      Disclaimer
                    </Link>
                    {/* <Link href={telegramLink} isExternal>
                      <Icon icon="simple-icons:telegram" width={16} height={16} />
                    </Link> */}
                  </Flex>
                </Flex>
              </Box>
              <HStack spacing={2} align="center">
                <TinyAction
                  icon="mdi:flag-outline"
                  label="Report an issue that is not relevant to a specific sacrifice"
                  leftLabel={
                    <Text fontSize="xs" color={textColor}>
                      Report
                    </Text>
                  }
                  placement="top-end"
                  onClick={() => {
                    setIsOpen(true)
                  }}
                />
              </HStack>
            </Flex>
          </VStack>
        </Container>
      </Box>

      {/*
        Render the AppKit-connected report modal once its bundle has loaded so
        the wallet connect flow works; until then fall back to the bare modal so
        the dialog still opens instantly on click. Only ever one is mounted (the
        AppKit one replaces the bare one), so there are never two overlapping
        dialogs bound to the same target.
      */}
      {ReportModalWithAppKit ? (
        <ReportModalWithAppKit
          title="Submit General Report"
          target={target as ProjectNetworkIdentifier | SacrificeData | null}
          onClose={() => {
            setIsOpen(false)
          }}
        />
      ) : (
        <ReportModal
          title="Submit General Report"
          target={target as ProjectNetworkIdentifier | SacrificeData | null}
          onClose={() => {
            setIsOpen(false)
          }}
        />
      )}

      {/*
        Agreements modal in review/view-only mode: the full 3-global + per-campaign
        disclaimer surface, readable without a connected wallet. The per-campaign
        text falls back to the generic sacrifice disclaimer when no project is
        selected.
      */}
      <AgreementsModal
        isOpen={isDisclaimerOpen}
        onClose={onDisclaimerClose}
        onComplete={onDisclaimerClose}
        mode="review"
        campaignDisclaimerText={selectedProject?.projectDisclaimerShort || YOU_ARE_SACRIFICING}
      />
    </>
  )
}

export default Footer
